import { setPermissionList } from 'app/store';
import { api, unwrapData } from '../../../app/store/api';
import { Address, AddressFormValues } from 'features/profile/types/address.types';
import { ApiResponse } from 'types/api-response.types';
import { UpdateUser, User } from 'features/profile/types/user.types';

export const userApi = api.injectEndpoints({
    endpoints: (build) => ({
        updateProfile: build.mutation<User, UpdateUser>({
            transformResponse: unwrapData,
            query: (user) => ({
                url: 'user/me',
                method: 'PATCH',
                body: user,
            }),
            invalidatesTags: (result) =>
                result ? [{ type: 'User' }, { type: 'Addresses' }] : [],
        }),

        userProfile: build.query<User, void>({
            transformResponse: unwrapData,
            query: () => ({
                url: 'user/me',
                method: 'GET',
            }),
            providesTags: ['User'],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;

                    if (!!data) {
                        dispatch(setPermissionList(data.permissions));
                    }
                } catch (error) {}
            },
        }),

        renewSecretKey: build.mutation<{ key: string }, void>({
            transformResponse: unwrapData,
            query: () => ({
                url: 'user/me/secret-key/renew',
                method: 'POST',
            }),
            invalidatesTags: (result) => (result ? [{ type: 'User' }] : []),
        }),

        toggleTwoverify: build.mutation<
            { two_factor_enabled: boolean },
            { is_active: boolean; password: string; repeat_password?: string }
        >({
            transformResponse: unwrapData,
            query: (body) => ({
                url: '/user/me/two-factor',
                method: 'PATCH',
                body,
            }),
            invalidatesTags: (result) => (result ? [{ type: 'User' }] : []),
        }),

        documentsStore: build.mutation<
            null,
            {
                passport: string;
                national: string;
                selfie?: string;
                statute?: string;
                paper?: string;
            }
        >({
            transformResponse: unwrapData,
            query: (body) => ({
                url: '/user/me/documents/store',
                method: 'POST',
                body,
            }),
            invalidatesTags: (result) => (result ? [{ type: 'User' }] : []),
        }),

        storeAddress: build.mutation<Address, AddressFormValues>({
            transformResponse: unwrapData,
            query: (body) => ({
                url: '/user/me/addresses',
                method: 'POST',
                body,
            }),
            invalidatesTags: (result) =>
                result ? [{ type: 'Addresses' }] : [],
        }),

        taxInquery: build.mutation<
            Address,
            { address_id: number }
        >({
            transformResponse: unwrapData,
            query: (body) => ({
                url: '/user/me/addresses/tax-inquiry',
                method: 'POST',
                body,
            }),
            invalidatesTags: (result) =>
                result ? [{ type: 'Addresses' }] : [],
        }),

        updateUserTelegram: build.mutation<any, { telegram: string }>({
            transformResponse: unwrapData,
            query: (body) => ({
                url: '/notifications/telegram',
                method: 'POST',
                body,
            }),
            invalidatesTags: (result) => (result ? [{ type: 'User' }] : []),
        }),
    }),
});

export const {
    useUpdateProfileMutation,
    useUserProfileQuery,
    useRenewSecretKeyMutation,
    useToggleTwoverifyMutation,
    useStoreAddressMutation,
    useTaxInqueryMutation,
    useDocumentsStoreMutation,
    useUpdateUserTelegramMutation,
} = userApi;
